import { View, Text, ScrollView, StyleSheet, RefreshControl, Pressable, Animated, Image } from 'react-native'
import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useDataLoading } from '../../src/hooks/useDataLoading'
import { useSafeAPI } from '../../src/hooks/useSafeAPI'
import { getProviderDisplay } from '../../src/utils/onboarding'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useRouter } from 'expo-router'
import { Zap, Smartphone, Wifi, ChevronRight, Plus, Search, Package, Info, X, CheckCircle2, TrendingDown, AlertTriangle } from 'lucide-react-native'
import { BottomSheetModal, BottomSheetBackdrop, BottomSheetView } from '@gorhom/bottom-sheet'
import { useAuth } from '../../src/auth/AuthProvider'
import { storage, USER_ID_KEY } from '../../src/utils/storage'
import { apiClient } from '../../src/api/client'
import { useHuntStats } from '../../src/hooks/useHuntStats'
import { colors, spacing, borderRadius, typography, shadows } from '../../src/theme'
import { haptics } from '../../src/utils/haptics'
import AnimatedItem from '../../src/components/AnimatedItem'
import ActivityFeed from '../../src/components/ActivityFeed'
import ContentContainer from '../../src/components/ContentContainer'
import { getProviderLogo } from '../../src/utils/providerLogos'
import PressableScale from '../../src/components/PressableScale'
import { DashboardSkeleton } from '../../src/components/Skeleton'
import { useResponsive } from '../../src/hooks/useResponsive'
import { toast } from '../../src/components/Toast'
import { appEvents } from '../../src/lib/events'
import { useFeedback } from '../../src/hooks/useFeedback'
import { sentryCaptureException } from '../../src/lib/sentry'
import { persistentStorage } from '../../src/lib/storageAdapter'
import { LinearGradient } from 'expo-linear-gradient'
import { getEnergyBreakdown, calcEnergyTotal, DEFAULT_KWH_YEAR, type TariffData } from '../../src/components/EnergyBreakdown'
import { computeQuoteSavings, computeEffectivePrice, cacheDashboardSavings, type BestMarketProvider } from '../../src/utils/savings'

export { ScreenErrorBoundary as ErrorBoundary } from '../../src/components/ScreenErrorBoundary'

function AnimatedNumber({ value, style, suffix = '', prefix = '' }: { value: number; style?: any; suffix?: string; prefix?: string }) {
  const [displayText, setDisplayText] = useState(`${prefix}0${suffix}`)

  useEffect(() => {
    const end = value
    const duration = 800
    const startTime = Date.now()
    let rafId: number

    const animate = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3)
      const current = Math.round(end * eased)
      setDisplayText(`${prefix}${current.toLocaleString('da-DK')}${suffix}`)
      if (progress < 1) rafId = requestAnimationFrame(animate)
    }

    animate()
    return () => cancelAnimationFrame(rafId)
  }, [value, prefix, suffix])

  return <Text style={style}>{displayText}</Text>
}

const getCategoryLabel = (cat: string) => {
  switch (cat) {
    case 'energy': return 'Strøm'
    case 'mobile': return 'Mobil'
    case 'internet': return 'Internet'
    default: return cat
  }
}

const getCategoryColor = (cat: string) => {
  switch (cat) {
    case 'energy': return { icon: colors.categories.energy, bg: colors.categoriesBg.energy }
    case 'mobile': return { icon: colors.categories.mobile, bg: colors.categoriesBg.mobile }
    case 'internet': return { icon: colors.categories.internet, bg: colors.categoriesBg.internet }
    default: return { icon: colors.text.muted, bg: colors.surface }
  }
}

const CategoryIcon = ({ category, size = 18 }: { category: string; size?: number }) => {
  const c = getCategoryColor(category)
  switch (category) {
    case 'energy': return <Zap size={size} color={c.icon} />
    case 'mobile': return <Smartphone size={size} color={c.icon} />
    case 'internet': return <Wifi size={size} color={c.icon} />
    default: return <Zap size={size} color={c.icon} />
  }
}

export default function DashboardScreen() {
  const { user, isLoading: authLoading } = useAuth()
  const router = useRouter()
  const { loading, refreshing, loadError, setLoading, setRefreshing, setLoadError } = useDataLoading()
  const { safe, hadError, reset: resetSafeError } = useSafeAPI('dashboard')

  const [quoteRequests, setQuoteRequests] = useState<any[]>([])
  const [quotes, setQuotes] = useState<any[]>([])
  const [contracts, setContracts] = useState<any[]>([])
  const [huntMessages, setHuntMessages] = useState<any[]>([])
  const [allInboxMessages, setAllInboxMessages] = useState<any[]>([])
  const [marketPlans, setMarketPlans] = useState<Record<string, any[]>>({})
  const [energyTariffsState, setEnergyTariffsState] = useState<TariffData | null>(null)
  const [billAverages, setBillAverages] = useState<Record<string, any>>({})
  const [currentProviders, setCurrentProviders] = useState<Record<string, any> | null>(null)
  const [switchInfoPopup, setSwitchInfoPopup] = useState<string | null>(null)
  const [oldProviderTooltip, setOldProviderTooltip] = useState<string | null>(null)
  const [betaBannerDismissed, setBetaBannerDismissed] = useState(
    persistentStorage.get('beta_banner_v1_dismissed') === 'true'
  )
const [cancelSwitchCategory, setCancelSwitchCategory] = useState<string | null>(null)
  const [cancelLoading, setCancelLoading] = useState(false)
  const [activityCount, setActivityCount] = useState<number | null>(null)
  const { isDesktop } = useResponsive()
  const switchInfoSheetRef = useRef<BottomSheetModal>(null)
  const cancelSwitchSheetRef = useRef<BottomSheetModal>(null)

  // Present/dismiss bottom sheets based on state
  useEffect(() => {
    if (switchInfoPopup) { switchInfoSheetRef.current?.present(); haptics.light() }
    else switchInfoSheetRef.current?.dismiss()
  }, [switchInfoPopup])

  useEffect(() => {
    if (cancelSwitchCategory) { cancelSwitchSheetRef.current?.present(); haptics.medium() }
    else cancelSwitchSheetRef.current?.dismiss()
  }, [cancelSwitchCategory])

  const renderBackdrop = useCallback(
    (props: any) => <BottomSheetBackdrop {...props} disappearsOnIndex={-1} appearsOnIndex={0} opacity={0.5} pressBehavior="close" />,
    []
  )

  const firstName = user?.full_name?.split(' ')[0] || ''

  // Pulse animation ref (effect runs after hasActiveHunt is derived)
  const pulseAnim = useRef(new Animated.Value(1)).current

  const loadDashboardData = useCallback(async (userIdArg?: string) => {
    try {
      setLoadError(false)
      const userId = userIdArg || user?.id || ''
      if (!userId) return

      const dedupFilter = { unique_per_provider: 'true' }
      resetSafeError()

      const [requestsRes, quotesRes, inboxRes, userRes, contractsRes, billHistoryRes] = await Promise.all([
        safe(apiClient.quotes.getRequests(userId), { data: [] } as any),
        safe(apiClient.quotes.list(userId), { data: [] } as any),
        safe(apiClient.inbox.list({ limit: '200' }), { data: { messages: [] } } as any),
        safe(apiClient.users.getMe(userId), { data: null } as any),
        safe(apiClient.contracts.list(userId), { data: [] } as any),
        safe(apiClient.contracts.billHistory(), { data: { averages: {} } } as any),
      ])
      if (hadError.current) setLoadError(true)
      const billAvgs = billHistoryRes.data?.averages || {}
      setBillAverages(billAvgs)

      // Merge current_providers with accepted-quote data
      const cp: Record<string, any> = { ...(userRes.data?.current_providers || {}) }
      for (const q of (quotesRes.data || [])) {
        if (q.status === 'accepted' && q.category && cp[q.category]?.status !== 'pending_switch') {
          const existingName = cp[q.category]?.provider_name
          cp[q.category] = {
            ...(cp[q.category] || {}),
            provider_name: q.provider,
            status: 'pending_switch',
            monthly_price: q.monthly_price,
            product_name: q.product_name,
            quote_id: q.id,
            ...(existingName && existingName.toLowerCase() !== q.provider.toLowerCase()
              ? { previous_provider: existingName } : {}),
          }
        }
      }

      const reqs = requestsRes.data || []
      const sortedReqs = [...reqs].sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      const reqWithData = sortedReqs.find((r: any) => {
        const ud = r.user_data as Record<string, any> | null
        return ud && ['energy', 'mobile', 'internet'].some(cat => {
          const plans = Array.isArray(ud[cat]) ? ud[cat] : ud[cat] ? [ud[cat]] : []
          return plans.some((p: any) => p.provider)
        })
      })
      if (reqWithData?.user_data) {
        for (const cat of ['energy', 'mobile', 'internet']) {
          if (cp[cat]?.status === 'pending_switch' && !cp[cat]?.previous_provider) {
            const catPlans = Array.isArray(reqWithData.user_data[cat]) ? reqWithData.user_data[cat] : reqWithData.user_data[cat] ? [reqWithData.user_data[cat]] : []
            const oldProvider = catPlans[0]?.provider
            if (oldProvider && oldProvider.toLowerCase() !== cp[cat].provider_name?.toLowerCase()) {
              cp[cat] = { ...cp[cat], previous_provider: oldProvider }
            }
          }
        }
      }
      setCurrentProviders(Object.keys(cp).length > 0 ? cp : null)
      setContracts(contractsRes.data || [])

      const activeReq = reqs.find((r: any) => ['pending', 'pending_approval', 'sent'].includes(r.status))
      const userData = activeReq?.user_data || {}
      const mobilePlan = Array.isArray(userData.mobile) ? userData.mobile[0] : userData.mobile
      const internetPlan = Array.isArray(userData.internet) ? userData.internet[0] : userData.internet
      const minGb = Number(mobilePlan?.desired_gb_dk) || undefined
      const internetNeeds = internetPlan?.needs as string | undefined
      const minSpeed = internetNeeds === 'premium' ? 300 : internetNeeds === 'standard' ? 100 : undefined

      const mobileFilter = { ...dedupFilter, ...(minGb ? { min_gb: String(minGb) } : {}) }
      const internetFilter = { ...dedupFilter, ...(minSpeed ? { min_speed: String(minSpeed) } : {}) }

      const [energyPub, mobilePub, internetPub] = await Promise.all([
        apiClient.publicPrices.cheapest('energy', 20, dedupFilter).catch(() => ({ data: { plans: [] } })),
        apiClient.publicPrices.cheapest('mobile', 20, mobileFilter).catch(() => ({ data: [] })),
        apiClient.publicPrices.cheapest('internet', 20, internetFilter).catch(() => ({ data: [] })),
      ])

      setQuoteRequests(reqs)
      setQuotes(quotesRes.data || [])
      const allMsgs = inboxRes.data?.messages || []
      setAllInboxMessages(allMsgs)
      setHuntMessages(allMsgs.filter((m: any) => m.quote_request_id))

      // Store raw market plans + tariffs (savings computed in useMemo below)
      const ePlans = energyPub.data?.plans || energyPub.data || []
      const mPlans = mobilePub.data || []
      const iPlans = internetPub.data || []
      setMarketPlans({ energy: ePlans, mobile: mPlans, internet: iPlans })
      setEnergyTariffsState(energyPub.data?.tariffs || null)
    } catch (error: any) {
      sentryCaptureException(error, { screen: 'dashboard' })
      toast.error('Noget gik galt — vores team er notificeret. Prøv igen.')
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    if (authLoading) return
    let cancelled = false
    ;(async () => {
      const userId = user?.id || await storage.get(USER_ID_KEY)
      if (!userId) {
        router.replace('/login' as any)
        return
      }
      if (!cancelled) loadDashboardData(userId)
    })()
    return () => { cancelled = true }
  }, [authLoading, user, loadDashboardData])

  // Refresh dashboard when inbox changes (matches Next.js window.addEventListener)
  useEffect(() => {
    const unsub = appEvents.on('inbox-updated', async () => {
      const userId = user?.id || await storage.get(USER_ID_KEY)
      if (userId) loadDashboardData(userId)
    })
    return () => { unsub() }
  }, [user])

  // Feedback triggers
  const { shouldFireTrigger, openFeedback } = useFeedback()
  useEffect(() => {
    const key = 'dashboard_visit_count'
    const count = parseInt(persistentStorage.get(key) || '0', 10) + 1
    persistentStorage.set(key, String(count))
    if (count === 1 && shouldFireTrigger('first_dashboard_visit')) {
      // First ever visit — beta early adopter prompt, 10s delay
      const t = setTimeout(() => openFeedback('first_dashboard_visit'), 10_000)
      return () => clearTimeout(t)
    }
    if (count >= 3 && shouldFireTrigger('dashboard_return')) {
      const t = setTimeout(() => openFeedback('dashboard_return', 'Hvad kunne vi gøre bedre?'), 15_000)
      return () => clearTimeout(t)
    }
  }, [])

  // Feedback: returning_user — 3+ days since account creation, 20s delay
  useEffect(() => {
    if (!user?.created_at) return
    const days = (Date.now() - new Date(user.created_at).getTime()) / 86_400_000
    if (days >= 3 && shouldFireTrigger('returning_user')) {
      const t = setTimeout(() => openFeedback('returning_user', 'Hvad holder dig tilbage fra at bruge appen mere?'), 20_000)
      return () => clearTimeout(t)
    }
  }, [user?.created_at])

  const onRefresh = useCallback(async () => {
    haptics.light()
    setRefreshing(true)
    const userId = user?.id || await storage.get(USER_ID_KEY)
    if (userId) await loadDashboardData(userId)
    setRefreshing(false)
  }, [loadDashboardData, user])

  // Offered providers — derived from quotes + market plans (same as Tilbud page)
  const offeredProviders = useMemo(() => {
    const offered: Record<string, Set<string>> = {}
    for (const q of quotes) {
      if (!offered[q.category]) offered[q.category] = new Set()
      offered[q.category].add(q.provider)
    }
    for (const [cat, plans] of Object.entries(marketPlans)) {
      for (const p of plans) {
        if (!offered[cat]) offered[cat] = new Set()
        offered[cat].add(p.provider)
      }
    }
    return offered
  }, [quotes, marketPlans])

  const activeQuoteRequests = quoteRequests.filter((r: any) => ['pending', 'pending_approval', 'sent'].includes(r.status))
  const hasActiveHunt = activeQuoteRequests.length > 0

  // Pulse animation for search icon — only when hunt is active
  useEffect(() => {
    if (!hasActiveHunt) return
    const pulse = Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnim, { toValue: 0.5, duration: 1000, useNativeDriver: true }),
        Animated.timing(pulseAnim, { toValue: 1, duration: 1000, useNativeDriver: true }),
      ])
    )
    pulse.start()
    return () => pulse.stop()
  }, [pulseAnim, hasActiveHunt])

  const { hunts } = useHuntStats(quoteRequests, huntMessages, offeredProviders)
  const activeCategories = new Set<string>()
  activeQuoteRequests.forEach((r: any) => r.categories?.forEach((c: string) => activeCategories.add(c)))
  const allCategoriesActive = activeCategories.size >= 3

  // Most recent request with user_data
  const sortedRequests = [...quoteRequests].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
  const latestRequestWithData = sortedRequests.find(r => {
    const ud = r.user_data as Record<string, any[]> | null
    if (!ud) return false
    return ['energy', 'mobile', 'internet'].some(cat => Array.isArray(ud[cat]) && ud[cat].some((p: any) => p.provider))
  })

  // Parse binding from string like "6 mdr", "Ingen binding" → number
  const parseBindingMonths = (v: any): number | undefined => {
    if (v == null) return undefined
    const s = String(v).trim().toLowerCase()
    if (!s) return undefined
    if (s.includes('ingen') || s === '0' || s === '0 mdr' || s === '0 md.') return 0
    const m = s.match(/(\d+)/)
    return m ? parseInt(m[1], 10) : undefined
  }

  // Merged plans (contracts + user_data)
  const mergedPlans = useMemo(() => {
    type Plan = {
      category: string; provider: string; price: number; source: 'contract' | 'onboarding';
      label?: string; name?: string; bindingMonths?: number; startDate?: string; acceptedAt?: string;
      contractId?: string; phoneNumber?: string; serviceAddress?: string; identifiersConfirmed?: boolean;
    }
    const plans: Plan[] = []
    const activeContracts = contracts.filter((c: any) => c.status !== 'cancelled')
    const confirmedKeys = new Set(activeContracts.map((c: any) => `${c.category}:${c.provider?.toLowerCase()}`))

    for (const cat of ['energy', 'mobile', 'internet']) {
      const catContracts = activeContracts.filter((c: any) => c.category === cat)
      catContracts.forEach((c: any, i: number) => {
        plans.push({
          category: cat, provider: c.provider, price: Number(c.monthly_price) || 0,
          source: 'contract', name: c.name,
          label: catContracts.length > 1 ? (i === 0 ? 'Primær' : `Aftale ${i + 1}`) : undefined,
          bindingMonths: c.binding_period_months ?? undefined,
          startDate: c.start_date ?? undefined,
          contractId: c.id,
          phoneNumber: c.phone_number ?? undefined,
          serviceAddress: c.service_address ?? undefined,
          identifiersConfirmed: !!c.identifiers_confirmed_at,
        })
      })

      const cp = currentProviders?.[cat]
      if (cp?.status === 'pending_switch' && cp?.provider_name) {
        const hasContractForSwitch = catContracts.some((c: any) => c.provider?.toLowerCase() === cp.provider_name.toLowerCase())
        if (!hasContractForSwitch) {
          plans.push({
            category: cat, provider: cp.provider_name, price: Number(cp.monthly_price) || 0,
            source: 'contract', name: cp.product_name,
            acceptedAt: cp.accepted_at ?? undefined,
            bindingMonths: parseBindingMonths(cp.contract_length),
          })
          confirmedKeys.add(`${cat}:${cp.provider_name.toLowerCase()}`)
        }
      }

      if (latestRequestWithData?.user_data) {
        const ud = latestRequestWithData.user_data as Record<string, any>
        const catPlans = Array.isArray(ud[cat]) ? ud[cat] : ud[cat] ? [ud[cat]] : []
        const unconfirmed = catPlans.filter((p: any) => !p.provider || !confirmedKeys.has(`${cat}:${p.provider.toLowerCase()}`))
        unconfirmed.forEach((p: any, i: number) => {
          const billPrice = billAverages[cat]?.confirmed_current_price?.monthly_price
          plans.push({
            category: cat, provider: getProviderDisplay(p.provider, cat), price: billPrice ?? (Number(p.confirmed_price || p.price) || 0),
            source: 'onboarding',
            label: unconfirmed.length > 1 ? (i === 0 ? 'Primær' : `Aftale ${i + 1}`) : undefined,
          })
        })
      }
    }
    return plans
  }, [contracts, latestRequestWithData, currentProviders, billAverages])

  const planCount = mergedPlans.length
  // Only sum current actual cost — exclude pending switch new providers (user still pays old provider)
  const monthlyTotal = mergedPlans.reduce((sum, p) => {
    const cp = currentProviders?.[p.category]
    const isPendingNew = cp?.status === 'pending_switch' && cp?.provider_name?.toLowerCase() === p.provider.toLowerCase()
    return isPendingNew ? sum : sum + p.price
  }, 0)
  // Calculate what it will be after pending switches complete
  const hasPendingSwitch = ['energy', 'mobile', 'internet'].some(cat => currentProviders?.[cat]?.status === 'pending_switch')
  const monthlyAfterSwitch = hasPendingSwitch ? (() => {
    const excludeIndices = new Set<number>()
    for (const cat of ['energy', 'mobile', 'internet']) {
      const cp = currentProviders?.[cat]
      if (cp?.status !== 'pending_switch') continue
      // Prefer linked_contract_id from accepted quote (exact match from provider email)
      const acceptedQuote = quotes.find((q: any) => q.status === 'accepted' && q.category === cat)
      const linkedId = acceptedQuote?.linked_contract_id
      if (linkedId) {
        // Find the specific contract being replaced (match by contract ID only — provider name may differ between contract and mergedPlan)
        const linkedContract = contracts.find((c: any) => c.id === linkedId)
        if (linkedContract) {
          const idx = mergedPlans.findIndex(p => p.category === cat && p.provider.toLowerCase() === linkedContract.provider?.toLowerCase())
          if (idx >= 0) { excludeIndices.add(idx); continue }
        }
      }
      // Fallback: replace the previous provider's plan (the one being switched FROM)
      const prevProvider = cp.previous_provider?.toLowerCase()
      if (prevProvider) {
        const idx = mergedPlans.findIndex(p => p.category === cat && p.provider.toLowerCase() === prevProvider)
        if (idx >= 0) { excludeIndices.add(idx); continue }
      }
      // Last resort: replace the most expensive old plan in this category
      let maxPrice = -1, maxIdx = -1
      mergedPlans.forEach((p, i) => {
        if (p.category === cat && p.provider.toLowerCase() !== cp.provider_name?.toLowerCase() && p.price > maxPrice) {
          maxPrice = p.price; maxIdx = i
        }
      })
      if (maxIdx >= 0) excludeIndices.add(maxIdx)
    }
    return mergedPlans.reduce((sum, p, i) => excludeIndices.has(i) ? sum : sum + p.price, 0)
  })() : 0

  // Current agreements — same data source as Tilbud page (user_data + billAverages)
  // This ensures savings calculations use identical baseline prices
  const currentAgreements = useMemo(() => {
    const active = quoteRequests.find((r: any) => ['pending', 'pending_approval', 'sent'].includes(r.status))
    if (!active?.user_data) return {} as Record<string, { price: number; yearlyKwh?: number }>
    const result: Record<string, { price: number; yearlyKwh?: number }> = {}
    for (const [cat, data] of Object.entries(active.user_data as Record<string, any>)) {
      const plans = Array.isArray(data) ? data : [data]
      const plan = plans[0]
      if (!plan || typeof plan !== 'object') continue
      const billPrice = billAverages[cat]?.confirmed_current_price?.monthly_price
      const confirmedPrice = plan.confirmed_price ? Number(plan.confirmed_price) : undefined
      const price = billPrice ?? confirmedPrice ?? (Number(plan.price) || 0)
      if (price > 0) result[cat] = { price, yearlyKwh: cat === 'energy' ? (Number(plan.yearly_kwh) || undefined) : undefined }
    }
    return result
  }, [quoteRequests, billAverages])

  // Savings per non-accepted category — ORIGINAL inline logic.
  // Also tracks bestPerCategory for chat-agent context sync.
  const { savingsByCategory, bestPerCategory } = useMemo(() => {
    const result: Record<string, number> = {}
    const best: Record<string, BestMarketProvider> = {}
    const acceptedCats = new Set(
      quotes.filter((q: any) => q.status === 'accepted').map((q: any) => q.category)
    )
    for (const cat of ['energy', 'mobile', 'internet']) {
      if (acceptedCats.has(cat)) continue
      const agreement = currentAgreements[cat]
      if (!agreement || agreement.price <= 0) continue
      const yearlyKwh = cat === 'energy' ? agreement.yearlyKwh : undefined

      // Personalized quotes — computeQuoteSavings handles tariff recalc via details
      const catQuotes = quotes.filter((q: any) => q.category === cat && q.status !== 'accepted')
      const personalProviders = new Set(catQuotes.map((q: any) => q.provider?.toLowerCase()))
      let bestSaving = 0
      let bestWinner: BestMarketProvider | null = null
      for (const q of catQuotes) {
        const s = computeQuoteSavings(agreement.price, q, energyTariffsState, yearlyKwh) || 0
        if (s > bestSaving) {
          bestSaving = s
          bestWinner = {
            provider: q.provider,
            monthly_price: Math.round(computeEffectivePrice(q, energyTariffsState, yearlyKwh)),
            yearly_savings: s,
          }
        }
      }

      // Market plans — compute effective price directly, same as Tilbud's publicPlanToQuote
      for (const p of (marketPlans[cat] || [])) {
        if (personalProviders.has(p.provider?.toLowerCase())) continue
        let price: number
        if (cat === 'energy' && energyTariffsState) {
          const bd = getEnergyBreakdown(
            { details: { subscription: p.monthly_subscription, surcharge_ore: p.kwh_surcharge } },
            energyTariffsState, yearlyKwh
          )
          price = bd ? calcEnergyTotal(bd, energyTariffsState, yearlyKwh || DEFAULT_KWH_YEAR) : Number(p.monthly_price || 0)
        } else {
          price = Number(p.monthly_price ?? p.monthly_subscription ?? 0)
        }
        if (price <= 0 || price >= agreement.price) continue
        const s = Math.round((agreement.price - price) * 12)
        if (s > bestSaving) {
          bestSaving = s
          bestWinner = {
            provider: p.provider,
            monthly_price: Math.round(price),
            yearly_savings: s,
          }
        }
      }

      if (bestSaving > 0) {
        result[cat] = bestSaving
        if (bestWinner) best[cat] = bestWinner
      }
    }
    return { savingsByCategory: result, bestPerCategory: best }
  }, [currentAgreements, quotes, marketPlans, energyTariffsState])

  const potentialSavings = Object.values(savingsByCategory).reduce((sum, s) => sum + s, 0)

  // Cache savings state so chat-tab can send it as dashboard_state to backend.
  // Always cache — chat reads from cache and falls back if empty.
  useEffect(() => {
    cacheDashboardSavings({
      totalSavings: potentialSavings,
      savingsByCategory,
      bestPerCategory,
    })
  }, [potentialSavings, savingsByCategory, bestPerCategory])

  // Accepted savings — for stats strip + switch info
  const acceptedSavings = useMemo(() => {
    const result: Record<string, number> = {}
    for (const q of quotes.filter((q: any) => q.status === 'accepted')) {
      if (!q.category) continue
      const yearlyKwh = q.category === 'energy' ? currentAgreements[q.category]?.yearlyKwh : undefined
      const s = computeQuoteSavings(currentAgreements[q.category]?.price || 0, q, energyTariffsState, yearlyKwh) || 0
      if (s > 0) result[q.category] = s
    }
    return result
  }, [quotes, currentAgreements, energyTariffsState])

  const totalAcceptedSavings = Object.values(acceptedSavings).reduce((sum, s) => sum + s, 0)

  // "Mission accomplished" state — all categories optimized, nothing left to hunt
  const allOptimized =
    potentialSavings === 0 &&
    quotes.some((q: any) => q.status === 'accepted') &&
    !hasPendingSwitch &&
    !hasActiveHunt

  const handleCancelSwitch = async (category: string) => {
    setCancelLoading(true)
    try {
      const res = await apiClient.quotes.cancelSwitch(category)
      toast.success(res.data?.email_sent ? 'Skiftet er annulleret — vi har sendt en annullering' : 'Skiftet er annulleret')
      setCancelSwitchCategory(null)
      appEvents.emitDebounced('inbox-updated')
      const userId = user?.id || await storage.get(USER_ID_KEY)
      if (userId) loadDashboardData(userId)
    } catch (err: any) {
      sentryCaptureException(err, { action: 'cancel_switch', category })
      toast.error('Kunne ikke annullere skiftet — vi er notificeret. Prøv igen.')
    } finally {
      setCancelLoading(false)
    }
  }


  // Match Next.js: if (authLoading) return null
  if (authLoading) return null

  if (loading) {
    return (
      <SafeAreaView style={styles.container} edges={[]}>
        <ContentContainer>
          <DashboardSkeleton />
        </ContentContainer>
      </SafeAreaView>
    )
  }

  return (
    <SafeAreaView style={styles.container} edges={[]}>
      <ContentContainer>
        <ScrollView
          style={styles.scroll}
          contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={colors.primary} />
        }
      >
        {/* Header */}
        <AnimatedItem index={0}>
        <View style={styles.header}>
          <View style={{ flex: 1 }}>
            <Text style={styles.greeting}>Hej{firstName ? ` ${firstName}` : ''}!</Text>
            <Text style={styles.subGreeting}>
              {hasActiveHunt
                ? 'Din agent kontakter selskaberne for dig'
                : allOptimized
                  ? 'Din agent har sikret dig de bedste aftaler'
                  : 'Din agent holder øje med dine udbydere'}
            </Text>
          </View>
          <View style={styles.headerButtons}>
            {hasActiveHunt && (
              <PressableScale style={styles.seeHuntsButton} onPress={() => router.push('/(tabs)/quotes' as any)}>
                <Search size={16} color={colors.primary} />
                <Text style={styles.seeHuntsText}>Din jagt</Text>
              </PressableScale>
            )}
            {!allCategoriesActive && !allOptimized && (
              <PressableScale style={styles.newHuntButton} onPress={() => router.push('/onboarding' as any)} haptic="light">
                <Plus size={16} color={colors.text.inverse} />
                <Text style={styles.newHuntText}>Ny jagt</Text>
              </PressableScale>
            )}
          </View>
        </View>
        </AnimatedItem>

        {/* Beta welcome banner — shown until dismissed */}
        {!betaBannerDismissed && (
          <View style={styles.betaBanner}>
            <View style={{ flex: 1 }}>
              <Text style={styles.betaBannerTitle}>Vi er helt nye ✨</Text>
              <Pressable
                accessibilityRole="button"
                onPress={() => openFeedback('first_dashboard_visit', 'Du er en af de første brugere — ser du noget der ikke virker, eller har du idéer? Vi vil meget gerne høre det 🙏')}
              >
                <Text style={styles.betaBannerBody}>Ser du noget der ikke virker, eller har du idéer? <Text style={{ color: colors.primary, fontWeight: '600' }}>Fortæl os →</Text></Text>
              </Pressable>
            </View>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Luk"
              onPress={() => { setBetaBannerDismissed(true); persistentStorage.set('beta_banner_v1_dismissed', 'true') }}
              style={{ padding: spacing.xs, minWidth: 44, minHeight: 44, alignItems: 'center', justifyContent: 'center' }}
            >
              <X size={18} color={colors.text.muted} />
            </Pressable>
          </View>
        )}

        {/* Error banner */}
        {loadError && (
          <View style={styles.errorBanner}>
            <Info size={16} color="#92400e" />
            <Text style={styles.errorText}>Noget gik galt — vores team er notificeret.</Text>
            <Pressable accessibilityRole="button" accessibilityLabel="Prøv igen" onPress={async () => { const uid = user?.id || await storage.get(USER_ID_KEY); if (uid) { setLoading(true); loadDashboardData(uid) } }}>
              <Text style={styles.errorRetry}>Prøv igen</Text>
            </Pressable>
          </View>
        )}

        {/* Savings Hero */}
        {(potentialSavings > 0 || totalAcceptedSavings > 0) ? (
          <AnimatedItem index={1}>
          <PressableScale accessibilityRole="button" accessibilityLabel="Se tilbud og besparelser" style={styles.savingsCardOuter} onPress={() => router.push('/(tabs)/quotes' as any)} haptic="medium">
            <LinearGradient
              colors={['#22c55e', '#059669']}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={styles.savingsCard}
            >
              <View style={styles.savingsInner}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.savingsLabel}>
                    {totalAcceptedSavings > 0 && potentialSavings > 0
                      ? 'Din agent har sparet dig'
                      : totalAcceptedSavings > 0
                        ? 'Din agent har sparet dig'
                        : 'Din agent kan spare dig'}
                  </Text>
                  <AnimatedNumber value={Math.round(totalAcceptedSavings + potentialSavings)} suffix=" kr/år" style={styles.savingsAmount} />
                  <Text style={styles.savingsSubtext}>
                    {totalAcceptedSavings > 0 && potentialSavings > 0
                      ? `${Math.round(totalAcceptedSavings).toLocaleString('da-DK')} kr sparet + ${Math.round(potentialSavings).toLocaleString('da-DK')} kr potentiel yderligere`
                      : totalAcceptedSavings > 0
                        ? 'Baseret på dit skifte til en bedre aftale'
                        : 'Baseret på dine nuværende aftaler vs. bedste tilbud'}
                  </Text>
                </View>
                <View style={styles.savingsArrow}>
                  <ChevronRight size={24} color={colors.text.inverse} />
                </View>
              </View>
            </LinearGradient>
          </PressableScale>
          </AnimatedItem>
        ) : null}

        {/* All optimized — "Mission accomplished" card */}
        {allOptimized && (
          <AnimatedItem index={1}>
          <LinearGradient
            colors={['#0d9488', '#4f46e5']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={[styles.savingsCard, { borderRadius: borderRadius.lg }]}
          >
            <View style={styles.savingsInner}>
              <View style={{ flex: 1 }}>
                <Text style={[styles.savingsLabel, { color: 'rgba(255,255,255,0.85)' }]}>Du er på de bedste aftaler</Text>
                {totalAcceptedSavings > 0 ? (
                  <>
                    <AnimatedNumber value={Math.round(totalAcceptedSavings)} suffix=" kr/år" style={styles.savingsAmount} />
                    <Text style={[styles.savingsSubtext, { color: 'rgba(255,255,255,0.75)' }]}>sparet vs. dine tidligere aftaler</Text>
                  </>
                ) : (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 4 }}>
                    <CheckCircle2 size={22} color={colors.text.inverse} />
                    <Text style={[styles.savingsAmount, { fontSize: 22 }]}>Alle aftaler optimeret</Text>
                  </View>
                )}
              </View>
            </View>
          </LinearGradient>
          </AnimatedItem>
        )}

        {/* Hunt in progress card — always show when hunt is active */}
        {hasActiveHunt && potentialSavings <= 0 && (
          <AnimatedItem index={1}>
          <PressableScale onPress={() => router.push('/(tabs)/quotes' as any)} haptic="light">
          <LinearGradient
            colors={['#3b82f6', '#4f46e5']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={styles.huntInProgressCard}
          >
            <View style={{ flex: 1 }}>
              <Text style={styles.huntProgressLabel}>Jagten er i gang</Text>
              <Text style={styles.huntProgressTitle}>Din agent kontakter selskaberne</Text>
              <Text style={styles.huntProgressSubtext}>Du får besked når tilbud begynder at komme ind</Text>
            </View>
            <Animated.View style={[styles.huntProgressIcon, { opacity: pulseAnim }]}>
              <Search size={24} color={colors.text.inverse} />
            </Animated.View>
          </LinearGradient>
          </PressableScale>
          </AnimatedItem>
        )}

        {/* Stats Strip */}
        {planCount > 0 && (
          <AnimatedItem index={2}>
          <View style={styles.statsStrip}>
            <View style={styles.statItem}>
              {hasPendingSwitch && monthlyAfterSwitch > 0 && monthlyAfterSwitch !== monthlyTotal && (
                <Text style={styles.statAfter}>→ {Math.round(monthlyAfterSwitch).toLocaleString('da-DK')} kr</Text>
              )}
              {monthlyTotal > 0 ? (
                <AnimatedNumber value={monthlyTotal} suffix=" kr" style={styles.statValue} />
              ) : (
                <Text style={styles.statValue}>–</Text>
              )}
              <Text style={styles.statLabel}>pr. måned</Text>
            </View>
            <View style={styles.statDivider} />
            <View style={styles.statItem}>
              <AnimatedNumber value={planCount} suffix="" style={styles.statValue} />
              <Text style={styles.statLabel}>aftaler</Text>
            </View>
            <View style={styles.statDivider} />
            <View style={styles.statItem}>
              {totalAcceptedSavings > 0 ? (
                <>
                  <AnimatedNumber value={totalAcceptedSavings} suffix=" kr/år" style={[styles.statValue, { color: colors.success }]} />
                  <Text style={styles.statLabel}>sparet</Text>
                </>
              ) : potentialSavings > 0 ? (
                <>
                  <AnimatedNumber value={potentialSavings} suffix=" kr/år" style={[styles.statValue, { color: colors.primary }]} />
                  <Text style={styles.statLabel}>mulig besparelse</Text>
                </>
              ) : (
                <>
                  <AnimatedNumber value={quotes.length} suffix=" nye" style={[styles.statValue, { color: colors.primary }]} />
                  <Text style={styles.statLabel}>tilbud</Text>
                </>
              )}
            </View>
          </View>
          </AnimatedItem>
        )}

        {/* Active Hunts */}
        {hunts.length > 0 && (
          <AnimatedItem index={3}>
          <View style={styles.section}>
            {hunts.map((hunt) => {
              const request = hunt.quoteRequest
              const totalProviders = hunt.totalProviders
              const responded = hunt.respondedProviders
              const progressPercent = totalProviders > 0 ? Math.round((responded / totalProviders) * 100) : 0

              return (
                <PressableScale
                  key={request.id}
                  style={styles.huntCard}
                  onPress={() => router.push('/(tabs)/quotes?request=' + request.id as any)}
                >
                  <View style={styles.huntIcons}>
                    {request.categories?.map((cat: string) => {
                      const c = getCategoryColor(cat)
                      return (
                        <View key={cat} style={[styles.huntIconBadge, { backgroundColor: c.bg }]}>
                          <CategoryIcon category={cat} size={14} />
                        </View>
                      )
                    })}
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.huntTitle}>
                      {request.categories?.map((c: string) => getCategoryLabel(c)).join(', ') || 'Tjenester'}
                    </Text>
                    <View style={styles.huntProgress}>
                      <Text style={styles.huntProgressText}>{responded}/{totalProviders} svar</Text>
                      <View style={styles.progressBar}>
                        <LinearGradient
                          colors={['#3b82f6', '#22c55e']}
                          start={{ x: 0, y: 0 }}
                          end={{ x: 1, y: 0 }}
                          style={[styles.progressFill, { width: `${Math.max(progressPercent, 5)}%` }]}
                        />
                      </View>
                    </View>
                  </View>
                  <ChevronRight size={16} color={colors.text.muted} />
                </PressableScale>
              )
            })}
          </View>
          </AnimatedItem>
        )}

        {/* Agreements */}
        <AnimatedItem index={3}>
        <View style={styles.section}>
          {mergedPlans.length === 0 ? (
            !hasActiveHunt ? (
              <View style={styles.emptyHeroCard}>
                <View style={styles.emptyHeroIcon}>
                  <Zap size={32} color={colors.primary} />
                </View>
                <Text style={styles.emptyHeroTitle}>Din agent er klar</Text>
                <Text style={styles.emptyHeroText}>
                  Start en jagt — din agent kontakter alle udbydere og finder de bedste aftaler til dig.
                </Text>
                <PressableScale style={styles.emptyHeroCta} onPress={() => router.push('/onboarding' as any)} haptic="light">
                  <Text style={styles.emptyHeroCtaText}>Start din første jagt</Text>
                </PressableScale>
              </View>
            ) : null
          ) : (
            <>
              <View style={styles.sectionHeader}>
                <Package size={16} color={colors.text.secondary} />
                <Text style={styles.sectionTitle}>Dine Aftaler</Text>
              </View>
              <View style={styles.agreementsGrid}>
                {mergedPlans.map((plan, idx) => {
                  const c = getCategoryColor(plan.category)
                  const annualSaving = savingsByCategory[plan.category] || 0
                  const cp = currentProviders?.[plan.category]
                  const hasPendingSwitch = cp?.status === 'pending_switch'
                  const isNewProvider = hasPendingSwitch && cp?.provider_name?.toLowerCase() === plan.provider.toLowerCase()
                  const isOldProvider = hasPendingSwitch && !isNewProvider

                  return (
                    <PressableScale
                      key={idx}
                      style={[styles.agreementCard, isDesktop ? { width: '23.5%' as any } : { width: '100%' as any }]}
                      onPress={() => router.push({ pathname: '/(tabs)/quotes', params: { category: plan.category } } as any)}
                      haptic="light"
                    >
                      <View style={styles.agreementHeader}>
                        {getProviderLogo(plan.provider) ? (
                          <Image source={getProviderLogo(plan.provider)!} style={{ width: 28, height: 28, borderRadius: 8 }} />
                        ) : (
                          <View style={[styles.agreementIconBg, { backgroundColor: c.bg }]}>
                            <CategoryIcon category={plan.category} size={16} />
                          </View>
                        )}
                        <Text style={styles.agreementCategory} numberOfLines={1}>
                          {getCategoryLabel(plan.category)}{plan.label ? ` · ${plan.label}` : ''}
                        </Text>
                      </View>
                      <View style={{ marginTop: spacing.xs }}>
                        <Text style={[styles.agreementProvider, { flex: 1 }]} numberOfLines={1}>{plan.provider}</Text>
                      </View>
                      {plan.source === 'contract' && plan.identifiersConfirmed && !isNewProvider && !isOldProvider && (
                        <Pressable
                          accessibilityRole="button"
                          accessibilityLabel="Aftale bekræftet"
                          style={[styles.confirmedBadge, { marginTop: spacing.xs }]}
                          onPress={() => {
                            const id = plan.phoneNumber
                              ? `Vi har bekræftet din aftale hos ${plan.provider} — abonnement registreret på nummer ${plan.phoneNumber}.`
                              : plan.serviceAddress
                              ? `Vi har bekræftet din aftale hos ${plan.provider} — tilmeldt adressen ${plan.serviceAddress}.`
                              : `Vi har bekræftet din aftale hos ${plan.provider}.`
                            toast.info(id)
                          }}
                        >
                          <Text style={styles.confirmedText}>Bekræftet</Text>
                          <Info size={10} color={colors.success} />
                        </Pressable>
                      )}
                      {plan.price > 0 && (
                        <View style={styles.priceRow}>
                          <Text style={styles.priceValue}>{Math.round(plan.price).toLocaleString('da-DK')}</Text>
                          <Text style={styles.priceUnit}> kr/md</Text>
                        </View>
                      )}
                      {isNewProvider && (() => {
                        const planKey = `${plan.category}:${plan.provider}`
                        const switchSavings = acceptedSavings[plan.category] || 0
                        return (
                          <View>
                          {switchSavings > 0 && (
                            <Text style={styles.switchSavingText}>Sparer {switchSavings.toLocaleString('da-DK')} kr/år</Text>
                          )}
                          <Pressable
                            accessibilityRole="button"
                            accessibilityLabel="Vis skiftestatus"
                            style={styles.switchBadge}
                            onPress={() => setSwitchInfoPopup(switchInfoPopup === planKey ? null : planKey)}
                          >
                            <Text style={styles.switchBadgeText} numberOfLines={1}>Skift i gang</Text>
                            <Info size={12} color={colors.warningDark} />
                          </Pressable>
                          </View>
                        )
                      })()}
                      {isOldProvider && (
                        <View style={{ position: 'relative' }}>
                          <Pressable
                            accessibilityRole="button"
                            style={[styles.switchBadge, { backgroundColor: '#f3f4f6' }]}
                            onPress={() => setOldProviderTooltip(oldProviderTooltip === plan.provider ? null : plan.provider)}
                          >
                            <Text style={[styles.switchBadgeText, { color: colors.text.muted }]} numberOfLines={1}>Vi klarer opsigelsen</Text>
                            <Info size={12} color={colors.text.muted} />
                          </Pressable>
                          {oldProviderTooltip === plan.provider && (
                            <View style={styles.oldProviderTooltip}>
                              <Text style={styles.oldProviderTooltipTitle}>Vi håndterer opsigelsen</Text>
                              <Text style={styles.oldProviderTooltipText}>
                                Du behøver ikke kontakte {plan.provider} selv. Vi sender opsigelsen på dine vegne og koordinerer skiftet, så din nuværende aftale afsluttes korrekt.
                              </Text>
                            </View>
                          )}
                        </View>
                      )}
                      {!isNewProvider && !isOldProvider && annualSaving > 0 && (
                        <Pressable
                          accessibilityRole="button"
                          accessibilityLabel="Se besparelse"
                          style={styles.savingBadge}
                          onPress={() => router.push({ pathname: '/(tabs)/quotes', params: { category: plan.category } } as any)}
                        >
                          <Text style={styles.savingBadgeText}>
                            Spar {annualSaving.toLocaleString('da-DK')} kr/år
                          </Text>
                        </Pressable>
                      )}
                    </PressableScale>
                  )
                })}
              </View>
            </>
          )}
        </View>
        </AnimatedItem>

        {/* Activity Feed */}
        {activityCount !== 0 && (
          <AnimatedItem index={4}>
          <View style={styles.section}>
            <ActivityFeed onLoad={(count) => setActivityCount(count)} inboxMessages={allInboxMessages} />
          </View>
          </AnimatedItem>
        )}
      </ScrollView>

      {/* Switch Info Bottom Sheet */}
      <BottomSheetModal
        ref={switchInfoSheetRef}
        snapPoints={['55%', '80%']}
        onDismiss={() => setSwitchInfoPopup(null)}
        backdropComponent={renderBackdrop}
        handleIndicatorStyle={{ backgroundColor: '#CBD5E1', width: 36, height: 4, borderRadius: 2 }}
        backgroundStyle={{ borderTopLeftRadius: 24, borderTopRightRadius: 24 }}
        containerStyle={isDesktop ? { left: 288 } as any : undefined}
        enablePanDownToClose
      >
        <BottomSheetView style={styles.modalContent}>
          {(() => {
            const popupCategory = switchInfoPopup?.split(':')[0] || ''
            const cp = currentProviders?.[popupCategory]
            if (!cp) return null
            const modalSavings = acceptedSavings[popupCategory] || 0
            const CatIcon = popupCategory === 'energy' ? Zap : popupCategory === 'mobile' ? Smartphone : Wifi
            return (
              <>
                <View style={styles.modalHeaderRow}>
                  <View style={styles.modalHeaderIcon}>
                    <CatIcon size={22} color={colors.primary} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.modalTitle}>Skift i gang</Text>
                    <Text style={styles.modalSubtitle}>Afventer udbyder</Text>
                  </View>
                </View>

                <View style={styles.modalInfoCard}>
                  <View style={styles.infoRow}>
                    <Text style={styles.infoLabel}>Ny udbyder</Text>
                    <Text style={styles.infoValue} numberOfLines={1}>{cp.provider_name}</Text>
                  </View>
                  {cp.monthly_price ? (
                    <View style={styles.infoRow}>
                      <Text style={styles.infoLabel}>Ny pris</Text>
                      <Text style={styles.infoValue}>{Math.round(Number(cp.monthly_price))} kr/md</Text>
                    </View>
                  ) : null}
                  {modalSavings > 0 && (
                    <View style={styles.infoRow}>
                      <Text style={styles.infoLabel}>Besparelse</Text>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                        <TrendingDown size={14} color={colors.success} />
                        <Text style={[styles.infoValue, { color: colors.success }]}>{modalSavings.toLocaleString('da-DK')} kr/år</Text>
                      </View>
                    </View>
                  )}
                  {cp.accepted_at && (
                    <View style={styles.infoRow}>
                      <Text style={styles.infoLabel}>Accepteret</Text>
                      <Text style={styles.infoValue}>{new Date(cp.accepted_at).toLocaleDateString('da-DK')}</Text>
                    </View>
                  )}
                </View>

                <View style={styles.modalInfoNote}>
                  <Info size={14} color={colors.primary} style={{ marginTop: 1 }} />
                  <Text style={styles.modalInfoNoteText}>
                    Vi håndterer skiftet på dine vegne. Du får besked i indbakken når skiftet er gennemført. Du har 14 dages fortrydelsesret.
                  </Text>
                </View>

                <View style={styles.modalDivider} />

                <View style={styles.modalButtons}>
                  <Pressable
                    style={styles.modalCancelBtn}
                    onPress={() => setSwitchInfoPopup(null)}
                  >
                    <Text style={styles.modalCancelBtnText}>Luk</Text>
                  </Pressable>
                  <Pressable
                    style={styles.modalDangerBtn}
                    onPress={() => { setSwitchInfoPopup(null); setCancelSwitchCategory(popupCategory) }}
                  >
                    <Text style={styles.modalDangerBtnText}>Annuller skift</Text>
                  </Pressable>
                </View>
              </>
            )
          })()}
        </BottomSheetView>
      </BottomSheetModal>

      {/* Cancel Switch Bottom Sheet */}
      <BottomSheetModal
        ref={cancelSwitchSheetRef}
        snapPoints={['42%']}
        onDismiss={() => setCancelSwitchCategory(null)}
        backdropComponent={renderBackdrop}
        handleIndicatorStyle={{ backgroundColor: '#CBD5E1', width: 36, height: 4, borderRadius: 2 }}
        backgroundStyle={{ borderTopLeftRadius: 24, borderTopRightRadius: 24 }}
        containerStyle={isDesktop ? { left: 288 } as any : undefined}
        enablePanDownToClose
      >
        <BottomSheetView style={styles.modalContent}>
          <View style={styles.modalWarnIcon}>
            <AlertTriangle size={26} color={colors.warningDark} />
          </View>
          <Text style={[styles.modalTitle, { textAlign: 'center' }]}>Annuller skift?</Text>
          <Text style={[styles.modalText, { textAlign: 'center' }]}>
            Vi sender en annullering til{' '}
            <Text style={{ fontWeight: '700', color: colors.text.primary }}>{currentProviders?.[cancelSwitchCategory || '']?.provider_name}</Text>
            {currentProviders?.[cancelSwitchCategory || '']?.previous_provider ? (
              <Text> og gendanner din aftale hos <Text style={{ fontWeight: '700', color: colors.text.primary }}>{currentProviders?.[cancelSwitchCategory || '']?.previous_provider}</Text>.</Text>
            ) : <Text>.</Text>}
          </Text>
          <View style={styles.modalDivider} />
          <View style={styles.modalButtons}>
            <Pressable style={styles.modalCancelBtn} onPress={() => setCancelSwitchCategory(null)} disabled={cancelLoading}>
              <Text style={styles.modalCancelBtnText}>Fortryd</Text>
            </Pressable>
            <Pressable
              style={[styles.modalDangerBtn, cancelLoading && { opacity: 0.6 }]}
              onPress={() => { haptics.warning(); cancelSwitchCategory && handleCancelSwitch(cancelSwitchCategory) }}
              disabled={cancelLoading}
            >
              <Text style={styles.modalDangerBtnText}>
                {cancelLoading ? 'Annullerer...' : 'Ja, annuller'}
              </Text>
            </Pressable>
          </View>
        </BottomSheetView>
      </BottomSheetModal>
      </ContentContainer>
    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.surface },
  scroll: { flex: 1 },
  scrollContent: { padding: spacing.md, gap: spacing.lg, paddingBottom: 100 },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', gap: spacing.sm },
  greeting: { ...typography.h1, color: colors.text.primary },
  subGreeting: { ...typography.caption, color: colors.text.secondary, marginTop: 2 },
  headerButtons: { flexDirection: 'row', gap: spacing.xs, flexShrink: 0 },
  seeHuntsButton: {
    flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 12, paddingVertical: 8,
    backgroundColor: colors.primarySubtle, borderWidth: 1, borderColor: colors.primary,
    borderRadius: borderRadius.sm,
  },
  seeHuntsText: { ...typography.small, color: colors.primary, fontWeight: '600' },
  newHuntButton: {
    flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 12, paddingVertical: 8,
    backgroundColor: colors.primary, borderRadius: borderRadius.sm,
  },
  newHuntText: { ...typography.small, color: colors.text.inverse, fontWeight: '600' },
  betaBanner: {
    flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm,
    padding: spacing.sm + 2, marginBottom: spacing.xs,
    backgroundColor: colors.primaryLight, borderRadius: borderRadius.md,
    borderWidth: 1, borderColor: '#bfdbfe',
  },
  betaBannerTitle: { ...typography.captionBold, color: colors.primaryDark, marginBottom: 2 },
  betaBannerBody: { ...typography.small, color: colors.text.secondary, lineHeight: 18 },
  errorBanner: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.xs, padding: spacing.sm,
    backgroundColor: colors.warningLight, borderRadius: borderRadius.md, borderWidth: 1, borderColor: colors.warningMuted,
  },
  errorText: { flex: 1, ...typography.small, color: colors.warningDark },
  errorRetry: { ...typography.captionBold, color: colors.warningDark },
  // Hero savings card — biggest visual impact
  savingsCardOuter: {
    borderRadius: borderRadius.xl, overflow: 'hidden',
    ...shadows.colored(colors.success),
  },
  savingsCard: {
    borderRadius: borderRadius.xl, padding: spacing.lg,
  },
  savingsInner: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  savingsLabel: { ...typography.label, color: 'rgba(255,255,255,0.85)' },
  savingsAmount: { ...typography.displayLg, color: colors.text.inverse, marginVertical: 4 },
  savingsSubtext: { ...typography.caption, color: 'rgba(255,255,255,0.85)' },
  savingsArrow: {
    width: 48, height: 48, borderRadius: borderRadius.lg, backgroundColor: 'rgba(255,255,255,0.2)',
    alignItems: 'center', justifyContent: 'center',
  },
  huntInProgressCard: {
    borderRadius: borderRadius.xl, padding: spacing.lg,
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    ...shadows.colored(colors.primary),
  },
  huntProgressLabel: { ...typography.label, color: 'rgba(255,255,255,0.85)' },
  huntProgressTitle: { ...typography.bodyBold, color: colors.text.inverse, marginVertical: 2 },
  huntProgressSubtext: { ...typography.caption, color: 'rgba(255,255,255,0.85)' },
  huntProgressIcon: {
    width: 48, height: 48, borderRadius: borderRadius.lg, backgroundColor: 'rgba(255,255,255,0.2)',
    alignItems: 'center', justifyContent: 'center',
  },
  // Stats strip
  statsStrip: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-around',
    paddingVertical: spacing.sm, paddingHorizontal: spacing.xs,
    backgroundColor: colors.background, borderRadius: borderRadius.md,
    borderWidth: 1, borderColor: colors.border,
  },
  statItem: { flex: 1, alignItems: 'center' },
  statDivider: { width: 1, height: 32, backgroundColor: colors.borderLight },
  statLabel: { ...typography.small, color: colors.text.muted, marginTop: 2 },
  statValue: { ...typography.h3, color: colors.text.primary },
  statAfter: { ...typography.small, color: colors.success, marginTop: 1 },
  // Sections
  section: { gap: spacing.sm },
  sectionHeader: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
  sectionTitle: { ...typography.bodyBold, color: colors.text.primary },
  // Hunt card
  huntCard: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm, backgroundColor: colors.background,
    borderRadius: borderRadius.md, paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
    ...shadows.sm,
  },
  huntIcons: { flexDirection: 'row' },
  huntIconBadge: {
    padding: 4, borderRadius: borderRadius.sm, borderWidth: 2, borderColor: colors.background, marginLeft: -4,
  },
  huntTitle: { ...typography.captionBold, color: colors.text.primary },
  huntProgress: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginTop: 4 },
  huntProgressText: { ...typography.small, color: colors.text.secondary },
  progressBar: {
    flex: 1, height: 6, backgroundColor: colors.borderLight, borderRadius: 3, maxWidth: 120, overflow: 'hidden',
  },
  progressFill: { height: '100%', borderRadius: 3 },
  // Empty state
  emptyHeroCard: {
    backgroundColor: colors.primarySubtle, borderRadius: borderRadius.xl, padding: spacing.xl,
    alignItems: 'center', ...shadows.md,
  },
  emptyHeroIcon: {
    width: 64, height: 64, borderRadius: borderRadius.lg, backgroundColor: colors.primaryLight,
    alignItems: 'center', justifyContent: 'center', marginBottom: spacing.md,
  },
  emptyHeroTitle: { ...typography.bodyBold, color: colors.text.primary, textAlign: 'center' },
  emptyHeroText: { ...typography.caption, color: colors.text.secondary, textAlign: 'center', marginTop: spacing.xs },
  emptyHeroCta: {
    marginTop: spacing.md, paddingHorizontal: spacing.lg, paddingVertical: spacing.sm,
    backgroundColor: colors.primary, borderRadius: borderRadius.md,
  },
  emptyHeroCtaText: { ...typography.captionBold, color: colors.text.inverse },
  // Agreement cards
  agreementsGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  agreementCard: {
    backgroundColor: colors.background, borderRadius: borderRadius.md,
    padding: spacing.md, ...shadows.sm, borderWidth: 1, borderColor: colors.borderLight,
  },
  agreementHeader: { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 6 },
  agreementIconBg: { padding: 4, borderRadius: 8 },
  agreementCategory: { ...typography.label, color: colors.text.secondary, flex: 1 },
  agreementProvider: { ...typography.bodyBold, color: colors.text.primary, marginBottom: 2 },
  confirmedBadge: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    backgroundColor: colors.successLight, paddingHorizontal: 8, paddingVertical: 4,
    borderRadius: borderRadius.sm, alignSelf: 'flex-start',
    borderWidth: 1, borderColor: colors.successLight,
  },
  confirmedText: { ...typography.small, color: colors.success, fontWeight: '600', flexShrink: 1 },
  priceRow: { flexDirection: 'row', alignItems: 'baseline', marginTop: 2 },
  priceValue: { ...typography.bodyBold, color: colors.text.primary },
  priceUnit: { ...typography.small, color: colors.text.secondary },
  switchBadge: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 4,
    marginTop: 6, paddingHorizontal: 8, paddingVertical: 8, backgroundColor: colors.warningLight,
    borderRadius: borderRadius.sm, minHeight: 36,
  },
  switchBadgeText: { ...typography.small, color: colors.warningDark, fontWeight: '600' },
  oldProviderTooltip: {
    position: 'absolute' as const, left: 0, bottom: '100%' as any, marginBottom: 6, zIndex: 50,
    width: 220, backgroundColor: '#ffffff', borderRadius: 10, padding: 10,
    borderWidth: 1, borderColor: colors.border,
    shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.10, shadowRadius: 6, elevation: 4,
  },
  oldProviderTooltipTitle: { fontSize: 11, fontWeight: '600' as const, color: colors.text.primary, marginBottom: 4 },
  oldProviderTooltipText: { fontSize: 10, color: colors.text.secondary, lineHeight: 15 },
switchSavingText: { ...typography.small, color: colors.success, fontWeight: '500', textAlign: 'center' as const, marginTop: 4, marginBottom: 2 },
  cancelledText: { ...typography.small, color: colors.text.muted, fontStyle: 'italic', marginTop: 4 },
  savingBadge: {
    marginTop: 6, paddingHorizontal: 8, paddingVertical: 8, backgroundColor: colors.successLight,
    borderRadius: borderRadius.sm, alignItems: 'center', justifyContent: 'center', minHeight: 36,
  },
  savingBadgeText: { ...typography.small, color: colors.successDark, fontWeight: '600', textAlign: 'center' as const },
  activeText: { ...typography.small, color: colors.text.muted, marginTop: 4 },
  bindingGreen: { ...typography.small, color: colors.success, marginTop: 2 },
  bindingGray: { ...typography.small, color: colors.text.muted, marginTop: 2 },
  // Modals
  modalOverlay: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center', padding: spacing.md,
  },
  modalContent: {
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.sm,
    paddingBottom: spacing.lg,
    width: '100%',
  },
  modalHeaderRow: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.md,
  },
  modalHeaderIcon: {
    width: 44, height: 44, borderRadius: 22,
    backgroundColor: colors.primaryLight,
    alignItems: 'center', justifyContent: 'center',
  },
  modalWarnIcon: {
    width: 56, height: 56, borderRadius: 28,
    backgroundColor: colors.warningLight,
    alignItems: 'center', justifyContent: 'center', alignSelf: 'center', marginBottom: spacing.md,
  },
  modalTitle: { ...typography.h3, color: colors.text.primary },
  modalSubtitle: { ...typography.small, color: colors.text.secondary, marginTop: 2 },
  modalText: { ...typography.caption, color: colors.text.secondary, marginTop: spacing.sm, lineHeight: 20 },
  modalInfoCard: {
    gap: spacing.sm, padding: spacing.md, backgroundColor: colors.surface,
    borderRadius: borderRadius.md, marginBottom: spacing.md,
  },
  modalInfoNote: {
    flexDirection: 'row', alignItems: 'flex-start', gap: spacing.xs,
    padding: spacing.sm, backgroundColor: colors.primaryLight,
    borderRadius: borderRadius.md,
  },
  modalInfoNoteText: { ...typography.small, color: colors.primaryDark, flex: 1, lineHeight: 18 },
  modalDivider: { height: 1, backgroundColor: colors.border, marginVertical: spacing.md },
  modalButtons: { flexDirection: 'row', gap: spacing.sm },
  modalCancelBtn: {
    flex: 1, paddingVertical: 14, backgroundColor: 'transparent',
    borderWidth: 1, borderColor: colors.border, borderRadius: borderRadius.md, alignItems: 'center',
  },
  modalCancelBtnText: { ...typography.captionBold, color: colors.text.secondary },
  modalDangerBtn: {
    flex: 1, paddingVertical: 14, backgroundColor: colors.error,
    borderRadius: borderRadius.md, alignItems: 'center',
  },
  modalDangerBtnText: { ...typography.captionBold, color: colors.text.inverse },
  infoRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  infoLabel: { ...typography.small, color: colors.text.secondary },
  infoValue: { ...typography.captionBold, color: colors.text.primary, flexShrink: 1, textAlign: 'right' },
})
